import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../Images/aadyacolourlogo.png";

// Grade calculator
const calculateGrade = (percentage) => {
  if (percentage >= 85) return "A+";
  if (percentage >= 70) return "A";
  if (percentage >= 60) return "B+";
  if (percentage >= 50) return "B";
  return "C";
};

export const exportToPdf = async (studentRecords, fileName) => {
  const doc = new jsPDF();

  const loadImage = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = src;
      img.onload = () => resolve(img);
    });

  if (!Array.isArray(studentRecords)) {
    alert("Invalid student record format.");
    return;
  }

  const logoImg = await loadImage(AadyaLogo);

  for (let index = 0; index < studentRecords.length; index++) {
    const student = studentRecords[index];
    if (!student) continue;
    if (index > 0) doc.addPage();

    doc.addImage(logoImg, "PNG", 14, 8, 15, 15);
    doc.setFontSize(13);
    doc.text("Unit Test (2024-25)", 105, 25, null, null, "center");

    doc.setFillColor(255, 102, 0);
    doc.setTextColor(255, 255, 255);
    doc.rect(165, 10, 35, 7, "F");
    doc.setFontSize(8);
    doc.text("ACADEMIC PROFILE", 167, 15);

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Name: ${student.name || "N/A"}`, 14, 35);
    doc.text(`Grade / Section: ${student.class || "N/A"}`, 14, 40);
    doc.setFontSize(11);
    doc.text("Scholastic", 180, 40, null, null, "right");

    const scholasticBody = [];
    const subjectEndRows = [];

    const subjects = Object.entries(student.scholastic || {});
    const reorderedSubjects = [
      ...subjects.filter(([subject]) => subject.toLowerCase() !== "computer science"),
      ...subjects.filter(([subject]) => subject.toLowerCase() === "computer science"),
    ];

    let overallTotal = 0;

    reorderedSubjects.forEach(([subject, assessments]) => {
      const specs = Object.entries(assessments || {});
      const startRowIndex = scholasticBody.length;

      let subjectTotal = 0;

      specs.forEach(([spec, data], idx) => {
        const scored = parseFloat(data?.scored || 0);
        const maxMarks = parseFloat(data?.max_marks || 0);
        subjectTotal += scored > maxMarks ? maxMarks : scored;

        scholasticBody.push([
          {
            content: idx === 0 ? subject : "",
            rowSpan: 1,
          },
          spec,
          data?.max_marks ?? "",
          data?.scored ?? "",
          "", // total placeholder
          "", // grade placeholder
        ]);
      });

      const subjectGrade = calculateGrade(subjectTotal);

      if (scholasticBody[startRowIndex]) {
        scholasticBody[startRowIndex][4] = subjectTotal;
        scholasticBody[startRowIndex][5] = subjectGrade;
      }

      overallTotal += subjectTotal;

      subjectEndRows.push({
        index: startRowIndex + specs.length - 1,
        subject,
      });
    });

    autoTable(doc, {
      head: [["Subjects", "Assessment Spec", "Max Marks", "Scored", "Total", "Grade"]],
      body: scholasticBody,
      startY: 45,
      theme: "plain",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        textColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [255, 102, 0],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        lineWidth: 0.5,
      },
    });

    const totalMaxMarks = reorderedSubjects.reduce((sum, [, assessments]) => {
      return (
        sum +
        Object.values(assessments || {}).reduce(
          (acc, item) => acc + parseFloat(item?.max_marks || 0),
          0
        )
      );
    }, 0);

    const percentage = totalMaxMarks > 0 ? ((overallTotal / totalMaxMarks) * 100).toFixed(2) : "0.00";
    const overallGrade = calculateGrade(parseFloat(percentage));

    // === Tables Below ===
    const nextStartY = doc.lastAutoTable.finalY + 5;

    const observationData = Object.entries(student.observation || {}).map(
      ([obs, grade]) => [obs, grade]
    );

    autoTable(doc, {
      head: [["Observation", "Grade"]],
      body: observationData.length ? observationData : [["No data", ""]],
      startY: nextStartY,
      margin: { left: 14 },
      tableWidth: 80,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: { fillColor: [255, 102, 0], textColor: 255 },
    });

    const observationEndY = doc.lastAutoTable.finalY;

    const coScholasticData = Object.entries(student.co_scholastic || {}).map(
      ([subject, marks]) => [subject, marks]
    );

    autoTable(doc, {
      head: [["Co-Scholastic", "Marks Scored"]],
      body: coScholasticData.length ? coScholasticData : [["No data", ""]],
      startY: nextStartY,
      margin: { left: 100 },
      tableWidth: 95,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
      },
      headStyles: { fillColor: [255, 102, 0], textColor: 255 },
    });

    const coScholasticEndY = doc.lastAutoTable.finalY;

    // === Summary Box BELOW both tables ===
    const afterBothTablesY = Math.max(observationEndY, coScholasticEndY) + 10;

    const boxHeight = 14;
    const boxWidth = 35;
    const spacing = 20;
    const centerX = 105;

    const box1X = centerX - boxWidth - spacing; // Grand Total
    const box2X = centerX;                      // Percentage
    const box3X = centerX + boxWidth + spacing; // Grade

    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    doc.text("Grand Total", box1X + 4, afterBothTablesY);
    doc.text("Percentage", box2X + 6, afterBothTablesY);
    doc.text("Grade", box3X + 10, afterBothTablesY);

    doc.setFont(undefined, "normal");
    doc.rect(box1X, afterBothTablesY + 2, boxWidth / 2, boxHeight);
    doc.rect(box1X + boxWidth / 2, afterBothTablesY + 2, boxWidth / 2, boxHeight);
    doc.text(`${totalMaxMarks}`, box1X + 6, afterBothTablesY + 11);
    doc.text(`${overallTotal}`, box1X + boxWidth / 2 + 6, afterBothTablesY + 11);

    doc.rect(box2X, afterBothTablesY + 2, boxWidth, boxHeight);
    doc.setFont(undefined, "bold");
    doc.text(`${percentage}`, box2X + 10, afterBothTablesY + 11);

    doc.rect(box3X, afterBothTablesY + 2, boxWidth, boxHeight);
    doc.text(`${overallGrade}`, box3X + 13, afterBothTablesY + 11);
    doc.setFont(undefined, "normal");

    // === Footer ===
    const pageHeight = doc.internal.pageSize.height;
    const footerY = pageHeight - 22;

    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(`Remarks: ${student.remarks || " "}`, 14, footerY - 18);

    doc.text("Class Teacher", 20, footerY - 2);
    doc.text("Headmistress", 95, footerY - 2);
    doc.text("Parent/Guardian", 160, footerY - 2);

    doc.setFillColor(255, 102, 0);
    doc.rect(0, footerY, 210, 20, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(7);
    doc.text(
      "#123/1, Next to Goyal Orchid Greens Apartment, Hennur Bagalur Main Road, Kannur, Bangalore – 560077",
      5,
      footerY + 6
    );
    doc.text("Ph: 9663804909, 890432123 | WhatsApp No: 9605375909", 5, footerY + 11);
    doc.text("aadyaacademykannur@gmail.com | aadyaacademy.com", 5, footerY + 16);
  }

  doc.save(`${fileName}.pdf`);
};
